// Centralized API client. Does not change deployment URLs.

// For local testing, use localhost. For production, use the Azure URL.
export const API_BASE = "https://api.esiflow.com";
// export const API_BASE = "http://localhost:3001";

// Session token minted by the backend after the AAD sign-in exchange. Sent as a
// Bearer token so the backend can identify the user and enforce the subscription.
const STORAGE_SESSION = "esi_flow_session";
export const AUTH_REQUIRED_EVENT = "esi-flow:authentication-required";

export function getSessionToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_SESSION);
  } catch {
    return null;
  }
}

type FetchOptions = RequestInit & { parseJson?: boolean };

export async function apiFetch(input: string, init?: FetchOptions): Promise<Response> {
  const isAbsolute = /^https?:\/\//i.test(input);
  const url = isAbsolute ? input : `${API_BASE}${input.startsWith("/") ? "" : "/"}${input}`;

  // Attach the Bearer session token for our own backend only (never leak it to
  // absolute third-party URLs).
  const toOwnBackend = new URL(url).origin === new URL(API_BASE).origin;
  const token = toOwnBackend ? getSessionToken() : null;
  const headers = token
    ? { ...(init?.headers || {}), Authorization: `Bearer ${token}` }
    : init?.headers;

  const resp = await fetch(url, { ...init, headers });
  // Cached profile data can outlive the API session. Let the sign-in gate recover
  // instead of leaving every analysis to fail behind an apparently signed-in UI.
  // Ignore responses from an older session if the participant just signed in.
  if (resp.status === 401 && toOwnBackend && typeof window !== "undefined" && getSessionToken() === token) {
    window.dispatchEvent(new Event(AUTH_REQUIRED_EVENT));
  }
  return resp;
}

export async function apiJson<T>(input: string, init?: FetchOptions): Promise<T> {
  const resp = await apiFetch(input, {
    headers: {
      ...(init?.headers || {}),
      "Content-Type": "application/json",
    },
    ...init,
  });
  if (!resp.ok) throw new Error(`Request failed: ${resp.status}`);
  return (await resp.json()) as T;
}


